import Vue from "vue";
import { mapGetters, mapState } from "vuex";

import actionBarComponent from "@/components/child/actionBar/actionBar.vue";
import checkboxComponent from "@/components/child/form/checkbox.vue";
import dataTooltipComponent from "@/components/child/form/datatooltip.vue";
import decimalComponent from "@/components/child/form/decimal.vue";
import gridComponent from "@/components/child/grid/grid.vue";
import { GridAction } from "@/components/child/grid/gridAction";
import { GridColors, GridColumn, GridColumnType } from "@/components/child/grid/gridColumn";
import { Component } from "@/decorators";
import CapsulaModel from "@/models/capsulaModel";
import TipoCapsula from "@/models/enum/manipulacao/tipoCapsula";
import ProdutoModel from "@/models/produto/produtoModel";
import UsuarioModel from "@/models/usuarioModel";
import capsulaService from "@/services/capsulaService";
import { AppState, Getters } from "@/store/store";
import { changePosition } from "@/utils/common/array";

import "../crud.scss";

type ProdutoCor = {
    id: number;
    descricao: string;
    cor: string;
    ordem: number;
};

@Component({
    components: {
        actionBarComponent,
        checkboxComponent,
        dataTooltipComponent,
        decimalComponent,
        gridComponent,
    },
    computed: {
        ...mapState({
            usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        }),
        ...mapGetters(["HAS_PERMISSAO_PERFIL", "VALIDAR_PERMISSAO_SOMENTE_CONSULTA"] as Getters),
    },
})
export default class CapsulaEditComponent extends Vue {
    // State computed props
    usuarioLogado: UsuarioModel;
    HAS_PERMISSAO_PERFIL: (isRede: boolean, isFranqueador: boolean, isSuporte: boolean) => Promise<boolean>;
    VALIDAR_PERMISSAO_SOMENTE_CONSULTA: (telaDescricao: string) => boolean;

    private isAdmin = false;
    private podeEditar = true;
    private itemCompartilhado = false;
    somenteConsulta = false;

    model = new CapsulaModel();
    modelId = 0;

    produtosCores: ProdutoCor[] = [];
    produtosCoresGastrorresistente: ProdutoCor[] = [];
    produtosCoresVeganas: ProdutoCor[] = [];
    produtosCoresVeganasRevestidas: ProdutoCor[] = [];
    produtosCoresPullulan: ProdutoCor[] = [];
    produtos: ProdutoModel[] = [];

    extraActions: Array<object> = [];
    extraActionsUp: Array<GridAction> = [];
    extraActionsDown: Array<GridAction> = [];

    gridColumns: Array<GridColumn> = [
        new GridColumn("descricao", "Produto", GridColumnType.String),
        new GridColumn("cor", "Cor", GridColumnType.String),
        new GridColumn("ordem", "Prioridade", GridColumnType.String),
    ];

    private async load() {
        this.model = new CapsulaModel();
        this.model.ativo = true;

        if (this.modelId > 0) {
            try {
                const data = await capsulaService.get(this.modelId);
                this.model.updateFrom(data);

                this.itemCompartilhado = this.model.franquiaId == null && this.model.id !== null;
                this.podeEditar = (this.itemCompartilhado && this.isAdmin) || !this.itemCompartilhado;

                this.produtos = await capsulaService.produtosCores(this.modelId);

                this.produtosCores = this.produtos
                    .filter(p => p.tipoCapsula == TipoCapsula.Normal)
                    .sort((a, b) => (a.ordemCapsula > b.ordemCapsula ? 1 : -1))
                    .map((p, i) => ({
                        id: p.id,
                        descricao: p.descricao,
                        cor: p.cor != null ? p.cor.descricao : "",
                        ordem: i + 1,
                    }));

                this.produtosCoresGastrorresistente = this.produtos
                    .filter(p => p.tipoCapsula == TipoCapsula.Revestida)
                    .sort((a, b) => (a.ordemCapsula > b.ordemCapsula ? 1 : -1))
                    .map((p, i) => ({
                        id: p.id,
                        descricao: p.descricao,
                        cor: p.cor != null ? p.cor.descricao : "",
                        ordem: i + 1,
                    }));

                this.produtosCoresVeganas = this.produtos
                    .filter(p => p.tipoCapsula == TipoCapsula.Vegana)
                    .sort((a, b) => (a.ordemCapsula > b.ordemCapsula ? 1 : -1))
                    .map((p, i) => ({
                        id: p.id,
                        descricao: p.descricao,
                        cor: p.cor != null ? p.cor.descricao : "",
                        ordem: i + 1,
                    }));

                this.produtosCoresVeganasRevestidas = this.produtos
                    .filter(p => p.tipoCapsula == TipoCapsula.VeganaRevestida)
                    .sort((a, b) => (a.ordemCapsula > b.ordemCapsula ? 1 : -1))
                    .map((p, i) => ({
                        id: p.id,
                        descricao: p.descricao,
                        cor: p.cor != null ? p.cor.descricao : "",
                        ordem: i + 1,
                    }));

                this.produtosCoresPullulan = this.produtos
                    .filter(p => p.tipoCapsula == TipoCapsula.Pullulan)
                    .sort((a, b) => (a.ordemCapsula > b.ordemCapsula ? 1 : -1))
                    .map((p, i) => ({
                        id: p.id,
                        descricao: p.descricao,
                        cor: p.cor != null ? p.cor.descricao : "",
                        ordem: i + 1,
                    }));

                this.loadGrid();
            } catch {
                this.$router.back();
            }
        }
    }

    private async save() {
        const produtosOrdem = this.produtos.map(p => {
            let produto = new ProdutoModel();
            produto = p;
            let ordemCapsula = null;

            switch (p.tipoCapsula) {
                case TipoCapsula.Normal:
                    ordemCapsula = this.produtosCores.find(c => c.id === p.id)?.ordem;
                    break;
                case TipoCapsula.Revestida:
                    ordemCapsula = this.produtosCoresGastrorresistente.find(c => c.id === p.id)?.ordem;
                    break;
                case TipoCapsula.Vegana:
                    ordemCapsula = this.produtosCoresVeganas.find(c => c.id === p.id)?.ordem;
                    break;
                case TipoCapsula.VeganaRevestida:
                    ordemCapsula = this.produtosCoresVeganasRevestidas.find(c => c.id === p.id)?.ordem;
                    break;
                case TipoCapsula.Pullulan:
                    ordemCapsula = this.produtosCoresPullulan.find(c => c.id === p.id)?.ordem;
                    break;
                default:
                    ordemCapsula = null;
                    break;
            }

            produto.ordemCapsula = ordemCapsula;
            return produto;
        });

        if (this.itemCompartilhado && !this.isAdmin) {
            try {
                const sucesso = await capsulaService.salvarOrdem(produtosOrdem);
                if (sucesso) {
                    await this.$showInclusaoUpdate(this.modelId);
                    this.$router.back();
                }
            } catch {}
        } else {
            try {
                const isValid = await this.$validator.validateAll();
                if (isValid && this.podeEditar) {
                    try {
                        const sucesso = await capsulaService[!this.model.id ? "insert" : "update"](
                            this.model,
                            this.usuarioLogado.franquiaId,
                        );

                        if (sucesso) {
                            const sucesso = await capsulaService.salvarOrdem(produtosOrdem);
                            if (sucesso) {
                                await this.$showInclusaoUpdate(this.modelId);
                                this.$router.back();
                            }
                        }
                    } catch {}
                }
            } catch {}
        }
    }

    private cancel() {
        this.$router.back();
    }

    private loadGrid() {
        this.produtosCores = this.produtosCores.map((p, i) => ({ ...p, ordem: i + 1 }));
        this.produtosCoresGastrorresistente = this.produtosCoresGastrorresistente.map((p, i) => ({
            ...p,
            ordem: i + 1,
        }));
        this.produtosCoresVeganas = this.produtosCoresVeganas.map((p, i) => ({ ...p, ordem: i + 1 }));
        this.produtosCoresVeganasRevestidas = this.produtosCoresVeganasRevestidas.map((p, i) => ({
            ...p,
            ordem: i + 1,
        }));
        this.produtosCoresPullulan = this.produtosCoresPullulan.map((p, i) => ({ ...p, ordem: i + 1 }));

        this.addGridExtraActions();
    }

    private onExtraActions(name: string, campo: ProdutoCor) {
        if (name.trim() == "up") {
            this.produtosCores = changePosition(this.produtosCores, campo, "UP");
        }

        if (name.trim() == "down") {
            this.produtosCores = changePosition(this.produtosCores, campo, "DOWN");
        }

        this.loadGrid();
    }

    private onExtraActionsGastrorresistente(name: string, campo: ProdutoCor) {
        if (name.trim() == "up") {
            this.produtosCoresGastrorresistente = changePosition(this.produtosCoresGastrorresistente, campo, "UP");
        }

        if (name.trim() == "down") {
            this.produtosCoresGastrorresistente = changePosition(this.produtosCoresGastrorresistente, campo, "DOWN");
        }

        this.loadGrid();
    }

    private onExtraActionsVeganas(name: string, campo: ProdutoCor) {
        if (name.trim() == "up") {
            this.produtosCoresVeganas = changePosition(this.produtosCoresVeganas, campo, "UP");
        }

        if (name.trim() == "down") {
            this.produtosCoresVeganas = changePosition(this.produtosCoresVeganas, campo, "DOWN");
        }

        this.loadGrid();
    }

    private onExtraActionsVeganasRevestidas(name: string, campo: ProdutoCor) {
        if (name.trim() == "up") {
            this.produtosCoresVeganasRevestidas = changePosition(this.produtosCoresVeganasRevestidas, campo, "UP");
        }

        if (name.trim() == "down") {
            this.produtosCoresVeganasRevestidas = changePosition(this.produtosCoresVeganasRevestidas, campo, "DOWN");
        }

        this.loadGrid();
    }

    private onExtraActionsPullulan(name: string, campo: ProdutoCor) {
        if (name.trim() == "up") {
            this.produtosCoresPullulan = changePosition(this.produtosCoresPullulan, campo, "UP");
        }

        if (name.trim() == "down") {
            this.produtosCoresPullulan = changePosition(this.produtosCoresPullulan, campo, "DOWN");
        }

        this.loadGrid();
    }

    private addGridExtraActions() {
        this.extraActionsUp.splice(0);
        this.extraActionsDown.splice(0);
        this.extraActions.splice(0);

        const totalLinhas = Math.max(
            this.produtosCores.length,
            this.produtosCoresGastrorresistente.length,
            this.produtosCoresVeganas.length,
            this.produtosCoresVeganasRevestidas.length,
            this.produtosCoresPullulan.length,
        );

        for (let i = 0; i < totalLinhas; i++) {
            this.extraActionsUp.push(
                new GridAction("up", "Colocar para cima", "fa fa-arrow-up", GridColors.DARKGRAY, true),
            );
            this.extraActionsDown.push(
                new GridAction("down", "Colocar para baixo", "fa fa-arrow-down", GridColors.DARKGRAY, true),
            );
        }

        this.extraActions.push(this.extraActionsUp);
        this.extraActions.push(this.extraActionsDown);
    }

    private async mounted() {
        if (this.$route.params.id) {
            this.modelId = +this.$route.params.id;
        }

        this.isAdmin = await this.HAS_PERMISSAO_PERFIL(false, false, false);

        this.somenteConsulta = this.VALIDAR_PERMISSAO_SOMENTE_CONSULTA("capsulas");

        this.load();
    }
}
